// "Wer übt heute?" — pick which child plays (A6, siblings). Shown after login
// when the family has more than one child, and from the "Kind wechseln"
// button. A child with a PIN must enter it; the PIN is checked against that
// child only, so siblings may share one.

import { useState } from 'react';
import { ApiError, activateChildV3 } from '../api/client';
import type { ChildSummary } from '../api/client';
import type { StudentProfile } from '../api/types';

interface Props {
  children: ChildSummary[];
  activeId?: string;
  onPicked: (profile: StudentProfile) => void;
  /** Close without switching (only offered when a child is already active). */
  onCancel?: () => void;
}

const tile: React.CSSProperties = {
  minHeight: 96, padding: '16px 14px', borderRadius: 8, cursor: 'pointer',
  background: 'var(--card, #fff)', border: '1.5px solid rgba(60,50,40,0.2)',
  fontFamily: 'var(--serif)', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', gap: 4,
};

export default function ChildPicker({ children, activeId, onPicked, onCancel }: Props) {
  const [pinFor, setPinFor] = useState<ChildSummary | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function activate(child: ChildSummary, childPin?: string) {
    setBusy(true);
    setError('');
    try {
      const { profile } = await activateChildV3(child.id, childPin);
      onPicked(profile);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 401
        ? 'Die PIN stimmt nicht. Versuch es noch einmal.'
        : 'Das hat nicht geklappt. Bitte noch einmal tippen.');
      setPin('');
    } finally {
      setBusy(false);
    }
  }

  function choose(child: ChildSummary) {
    if (busy) return;
    if (child.has_pin) {
      setPinFor(child);
      setPin('');
      setError('');
    } else {
      void activate(child);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="picker-title" style={{
      position: 'fixed', inset: 0, background: 'var(--parchment)', zIndex: 9400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{ width: 'min(560px, 100%)', fontFamily: 'var(--serif)' }}>
        <h1 id="picker-title" style={{ fontFamily: 'var(--display)', fontSize: 30, textAlign: 'center', margin: '0 0 24px' }}>
          {pinFor ? `Hallo ${pinFor.name}!` : 'Wer übt heute?'}
        </h1>

        {!pinFor && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
            {children.map(c => (
              <button key={c.id} style={{ ...tile, borderColor: c.id === activeId ? 'var(--amber)' : tile.borderColor }}
                disabled={busy} onClick={() => choose(c)} aria-label={`${c.name}, Klasse ${c.klasse}`}>
                <span style={{ fontSize: 22, fontWeight: 600, color: 'var(--ink)' }}>{c.name}</span>
                <span style={{ fontSize: 13, color: 'var(--ink-muted)' }}>Klasse {c.klasse}{c.has_pin ? ' · 🔒' : ''}</span>
              </button>
            ))}
          </div>
        )}

        {pinFor && (
          <form onSubmit={e => { e.preventDefault(); if (pin.length === 4) void activate(pinFor, pin); }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <label htmlFor="child-pin" style={{ fontSize: 16 }}>Gib deine PIN ein:</label>
            <input id="child-pin" value={pin} autoFocus inputMode="numeric" maxLength={4}
              onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              style={{ fontSize: 28, letterSpacing: '0.5em', width: 150, textAlign: 'center', padding: 10,
                       borderRadius: 6, border: '1.5px solid rgba(60,50,40,0.3)' }} />
            <button type="submit" disabled={busy || pin.length !== 4} style={{
              minHeight: 48, padding: '10px 28px', border: 'none', borderRadius: 6,
              background: 'var(--amber)', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            }}>Los</button>
            <button type="button" onClick={() => setPinFor(null)} style={{
              background: 'none', border: 'none', color: 'var(--ink-muted)', textDecoration: 'underline', cursor: 'pointer',
            }}>Zurück</button>
          </form>
        )}

        {error && <p role="alert" style={{ color: 'var(--wrong)', textAlign: 'center', marginTop: 16 }}>{error}</p>}

        {onCancel && !pinFor && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button onClick={onCancel} style={{
              background: 'none', border: 'none', color: 'var(--ink-muted)', textDecoration: 'underline', cursor: 'pointer',
            }}>Abbrechen</button>
          </div>
        )}
      </div>
    </div>
  );
}
